import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import GoogleAuthButton from './GoogleAuthButton';
import LoadingSpinner from '../Common/LoadingSpinner';
// Remove WindowControls import - we'll handle it in the main layout

const LoginContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.dark.bg} 0%, 
    ${({ theme }) => theme.colors.dark.surface} 100%
  );
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  position: relative;
`;

// Remove WindowHeader - we'll handle window controls elsewhere

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  position: relative;
  min-height: 100vh;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    radial-gradient(circle at 25% 25%, ${({ theme }) => theme.colors.primary[500]} 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, ${({ theme }) => theme.colors.accent.purple} 0%, transparent 50%);
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};
  z-index: 10;
`;

const Logo = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.glow};
  
  &::before {
    content: '🤖';
    font-size: 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.dark.text}, 
    ${({ theme }) => theme.colors.gray[400]}
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  text-align: center;
  max-width: 400px;
  line-height: 1.6;
`;

const AuthSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  z-index: 10;
`;

const BypassSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.dark.border};
`;

const BypassButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.accent.orange}20;
  border: 1px solid ${({ theme }) => theme.colors.accent.orange};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.accent.orange};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent.orange}30;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const DevLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.dark.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  text-align: center;
`;

const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing['2xl']};
  max-width: 600px;
`;

const FeatureTag = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.dark.border};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary[500]};
    color: ${({ theme }) => theme.colors.primary[400]};
  }
`;

const ErrorMessage = styled(motion.div)`
  background: ${({ theme }) => theme.colors.accent.red}20;
  border: 1px solid ${({ theme }) => theme.colors.accent.red};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.accent.red};
  font-size: 0.875rem;
  text-align: center;
  max-width: 400px;
`;


const DisclaimerOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const DisclaimerModal = styled(motion.div)`
  background: ${({ theme }) => theme.colors.dark.elevated};
  border: 1px solid ${({ theme }) => theme.colors.primary[500]}40;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 
              0 0 0 1px ${({ theme }) => theme.colors.primary[500]}20;
  text-align: center;
`;

const ModalIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  animation: bounce 2s infinite;
  
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.dark.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 700;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[400]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ModalText = styled.p`
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  font-size: 1rem;
`;

const ModalButton = styled(motion.button)`
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[600]}, 
    ${({ theme }) => theme.colors.primary[500]}
  );
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  width: 100%;
  box-shadow: 0 4px 15px ${({ theme }) => theme.colors.primary[500]}40;
  
  &:hover {
    filter: brightness(1.1);
    transform: translateY(-1px);
  }
`;

const Highlight = styled.span`
  color: ${({ theme }) => theme.colors.accent.orange};
  font-weight: 600;
  background: ${({ theme }) => theme.colors.accent.orange}15;
  padding: 2px 6px;
  border-radius: 4px;
`;

const LoginScreen = () => {
  const { signInWithGoogle, loading, error } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  // Show disclaimer on first visit
  React.useEffect(() => {
    const hasSeenDisclaimer = localStorage.getItem('sarvik_demo_disclaimer');
    if (!hasSeenDisclaimer) {
      // Small delay for better UX entrance
      const timer = setTimeout(() => setShowDisclaimer(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseDisclaimer = () => {
    setShowDisclaimer(false);
    localStorage.setItem('sarvik_demo_disclaimer', 'true');
  };

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error('Sign-in failed:', err);
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleBypassLogin = () => {
    // Create a mock user for development
    const mockUser = {
      uid: 'dev-user-123',
      email: 'developer@sarvik.com',
      displayName: 'Developer User',
      photoURL: null,
      accessToken: 'dev-token'
    };

    // Store mock user and trigger auth state change
    localStorage.setItem('sarvik_user', JSON.stringify(mockUser));
    window.dispatchEvent(new CustomEvent('authStateChanged', {
      detail: { user: mockUser }
    }));
  };

  const features = [
    'Real-time Voice Assistant',
    'Smart Conversations',
    'Proactive Reminders',
    'Multi-device Sync',
    'Privacy-first Design',
    'Offline Capable'
  ];

  return (
    <LoginContainer>
      <BackgroundPattern />

      {/* Demo Disclaimer Modal */}
      {showDisclaimer && (
        <DisclaimerOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <DisclaimerModal
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <ModalIcon>👋</ModalIcon>
            <ModalTitle>Welcome to SARVIK (Demo)</ModalTitle>
            <ModalText>
              This is a <strong>web preview</strong> of the SARVIK Desktop App.
              <br />
              It demonstrates the core UI and features in a browser environment.
              <br /><br />
              Please use the <Highlight>Bypass Login (Demo)</Highlight> button to enter.
              <br /><br />
              <small style={{ opacity: 0.7 }}>
                The full AI backend is not connected as it requires high-performance resources
                unavailable due to <strong>demo hosting limitations</strong>.
              </small>
            </ModalText>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <ModalButton
                onClick={handleCloseDisclaimer}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Got it, Enter Demo
              </ModalButton>

              <a
                href="https://drive.google.com/file/d/1fh4rTH9ZLMRE9PBVz9jS6a8NPihGzj-A/view"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <ModalButton
                  as={motion.button}
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
                  whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.15)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  ▶️ Watch Working System Demo Video
                </ModalButton>
              </a>
            </div>
          </DisclaimerModal>
        </DisclaimerOverlay>
      )}

      <ContentArea>
        <LogoContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Logo />
          <Title>SARVIK Assistant</Title>
          <Subtitle>
            Your personal AI companion that understands, remembers, and assists you in real-time
          </Subtitle>
        </LogoContainer>

        <AuthSection
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {error && (
            <ErrorMessage
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </ErrorMessage>
          )}

          <GoogleAuthButton
            onClick={handleGoogleSignIn}
            loading={isSigningIn || loading}
          />

          {/* Demonstration Bypass Button */}
          <BypassSection>
            <DevLabel>Demonstration Mode</DevLabel>
            <BypassButton
              onClick={handleBypassLogin}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Bypass Login (Demo)
            </BypassButton>
          </BypassSection>

          <FeatureList>
            {features.map((feature, index) => (
              <FeatureTag key={feature}>
                {feature}
              </FeatureTag>
            ))}
          </FeatureList>
        </AuthSection>
      </ContentArea>
    </LoginContainer>
  );
};

export default LoginScreen;
